import { Box, Button, Grid, Group, List, Modal, Text } from '@mantine/core';
import { IconCopy, IconX } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import getErrorMessage from '../../helpers/getErrorMessage';
import useFetch from '../../helpers/useFetch';
import useIsSuperAdmin from '../../helpers/useIsSuperAdmin';
import ErrorMessage from '../ErrorMessage';
import SearchableCombobox from '../SearchableCombobox';
import SuperAdminArea from '../SuperAdmin/SuperAdminArea';

const isCopyable = (email) => Boolean(email?.id) && email.type !== 'custom' && email.editor !== 'unlayer';

const CopyEmailToDomainButton = ({ email }) => {
  const fetch = useFetch();
  const isSuperAdmin = useIsSuperAdmin();
  const user = useSelector((state) => state.auth.user);
  const { accountId, domainId } = useParams();

  const [pickerOpen, setPickerOpen] = useState(false);
  const [targetDomainId, setTargetDomainId] = useState(null);
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const targetDomainOptions = useMemo(() => {
    const currentAccount = user?.userRoles?.find((role) => role.account?.id === Number(accountId))?.account;

    return (currentAccount?.domains || [])
      .filter((domain) => domain.id !== Number(domainId))
      .map((domain) => ({ value: domain.id.toString(), label: domain.name }));
  }, [user?.userRoles, accountId, domainId]);

  if (!isSuperAdmin || !isCopyable(email) || targetDomainOptions.length === 0) {
    return null;
  }

  const closePicker = () => {
    setPickerOpen(false);
    setTargetDomainId(null);
    setError(null);
  };

  const submit = (event) => {
    event.preventDefault();
    setCopying(true);
    setError(null);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/emails/${email.id}/copyToDomain`, {
      method: 'POST',
      body: JSON.stringify({ targetDomainId: Number(targetDomainId) }),
    })
      .then((response) => {
        setPickerOpen(false);
        setTargetDomainId(null);
        setResult(response);
      })
      .catch(setError)
      .finally(() => setCopying(false));
  };

  const copiedEmailHref = result?.email?.id
    ? `#/accounts/${accountId}/domains/${result.targetDomainId}/emails/${result.email.id}/details`
    : undefined;

  return (
    <>
      <SuperAdminArea inline show tooltip="Copying an email between domains is available to super admins only.">
        <Button size="sm" color="cyan" onClick={() => setPickerOpen(true)}>
          <IconCopy size={14} /> Copy to another domain
        </Button>
      </SuperAdminArea>

      <Modal opened={pickerOpen} onClose={closePicker} title="Copy email to another domain">
        <form onSubmit={submit}>
          <Text size="sm" c="dimmed" mb="md">
            The copy is made from the last saved version of this email. It keeps the design, but all domain-specific
            links are removed and product blocks lose their template and product feed, because those belong to this
            domain.
          </Text>
          <Grid align="center">
            <Grid.Col span={4}>Target domain</Grid.Col>
            <Grid.Col span={8}>
              <SearchableCombobox
                options={targetDomainOptions}
                value={targetDomainId}
                onChange={setTargetDomainId}
                placeholder="Select target domain..."
              />
            </Grid.Col>
          </Grid>
          {error ? (
            <Box mt="sm">
              <ErrorMessage message={getErrorMessage(error)} />
            </Box>
          ) : null}
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={closePicker} leftSection={<IconX size={16} />}>
              Close
            </Button>
            <Button type="submit" disabled={!targetDomainId || copying} leftSection={<IconCopy size={16} />}>
              {copying ? 'Copying...' : 'Copy email'}
            </Button>
          </Group>
        </form>
      </Modal>

      <Modal opened={Boolean(result)} onClose={() => setResult(null)} title="Email copied">
        {result?.email?.id ? (
          <>
            <Text mb="md">
              <Text span fw={700}>
                {result.email.name}
              </Text>{' '}
              was copied to{' '}
              <Text span fw={700}>
                {result.targetDomainName}
              </Text>
              .
            </Text>
            <List size="sm" spacing="xs" mb="md">
              <List.Item>
                {result.strippedLinks > 0
                  ? `All ${result.strippedLinks} domain-specific link(s) were removed from the copy. Add the links for the target domain in the editor.`
                  : 'The copy has no domain-specific links to fix — this email had none.'}
              </List.Item>
              {result.productBlocks > 0 ? (
                <List.Item>
                  The email has {result.productBlocks} product block(s). Their template and product feed were cleared
                  and must be configured in the target domain, otherwise the email cannot be saved or sent.
                </List.Item>
              ) : null}
              {email.aiEmailOptions?.isGenerativeEmail ? (
                <List.Item>
                  This is a generative email. Its AI product feed and template were cleared, so Save stays disabled in
                  the target domain until you pick both again.
                </List.Item>
              ) : null}
              {result.missingCoupons?.length > 0 ? (
                <List.Item>
                  <Text span fw={700} c="red">
                    This email will send to nobody
                  </Text>{' '}
                  until {result.targetDomainName} has{' '}
                  {result.missingCoupons.length > 1 ? 'coupons named each of' : 'a coupon named'}{' '}
                  {result.missingCoupons.join(', ')}, since every recipient is filtered out when a coupon it references
                  does not exist there.
                </List.Item>
              ) : null}
              <List.Item>The sender and reply-to addresses now fall back to the target domain defaults.</List.Item>
            </List>
          </>
        ) : (
          <Text mb="md">
            The email was copied, but no confirmation details came back. Check the target domain to find it.
          </Text>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setResult(null)}>
            Close
          </Button>
          {copiedEmailHref ? (
            <Button component="a" href={copiedEmailHref} target="_blank" rel="noopener" onClick={() => setResult(null)}>
              Open copied email
            </Button>
          ) : null}
        </Group>
      </Modal>
    </>
  );
};

export default CopyEmailToDomainButton;
